import { Component, h, Prop, Host, Method, Listen, State } from "@stencil/core";
import { Product } from "@apis/responses/get-search-response";
import store from "@stores/search.store";
import classNames from "classnames";
import config from "../../../environment/environment-config";
/* TTRQ-52361 custom start */
import { trackGoogleAnalyticsEvent } from "../../../actions/analyticsActions";
import { createClickEventData } from "@apis/model/data-layer";
import { EventType } from "@apis/model/data-layer";
/* TTRQ-52361 custom end */

@Component({
  tag: "f-search-suggestions",
  styleUrl: "f-search-suggestions.scss",
  shadow: true,
})
export class FSearchSuggestions {
  private list: HTMLUListElement = null;
  private search: HTMLFSearchElement;

  @State() focusIndex = 0;

  @Prop() numberOfCharactersBeforeRequest = 3;
  @Prop() maximumNumberOfResults: number;
  @Prop({ mutable: true }) isLoading = false;
  @Prop() allResultsUrl = "/";
  @Prop() encodedContextPath = "";
  @Prop() allResultsLabel = "All Results";
  //TT CUSTOM START
  @Prop() ttCategoriesLabel = "Categories";
  @Prop() ttPriceLabel = "List price";
  @Prop() ttSkuLabel = "Article number: ";
  @Prop() ttEanLabel = "EAN: ";
  @Prop() ttOrigin: string;
  @Prop() ttMultiCartEnabled = config.cartConfig.multiCartEnabled;
  /* TTRQ-50439 custom start */
  @Prop() modalHeaderLabel: string;
  @Prop() modalBodyLabel: string;
  @Prop() modalAddToNewWishlistLabel: string;
  /* TTRQ-50439 custom end */
  /* TTRQ-52684 custom start */
  @Prop() createCartLabel: string;
  /* TTRQ-52684 custom end */
  /* TTRQ-54327 custom start */
  @Prop() cartModalHeaderLabel: string;
  @Prop() cartModalBodyLabel: string;
  @Prop() cartModalIncreaseLabel: string;
  @Prop() cartModalNewlineLabel: string;
  @Prop() cartModalCancelLabel: string;
  /* TTRQ-54327 custom end */
  /* TTRQ-48668 Custom Start */
  @Prop() addToCartLable: string;
  @Prop() addToWishtLable: string;
  /* TTRQ-48668 Custom End */
  //TT CUSTOM END

  componentDidRender(): void {
    (async () => {
      await customElements.whenDefined("f-search");
      this.search = document.querySelector("f-search");
    })();
  }

  // eslint-disable-next-line @stencil/prefer-vdom-listener
  @Listen("keydown")
  handleKeydown(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      (async () => await this.search.closeSearch())();
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (this.focusIndex + 1 < store.products.length) {
        this.focusIndex++;
      } else {
        this.focusIndex = 0;
      }
      this.focusOnNthSuggestion(this.focusIndex);
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (this.focusIndex > 0) {
        this.focusIndex--;
        this.focusOnNthSuggestion(this.focusIndex);
      } else {
        this.search.focusOnSearch();
      }
    }

    if (event.key === "Tab") {
      event.preventDefault();
      this.search.closeSearch();
    }
  }

  @Method()
  async focusOnNthSuggestion(n: number): Promise<void> {
    if (this.list !== null) {
      const suggestions = this.list.querySelectorAll("a");

      if (suggestions.length > 0) {
        suggestions[n].focus();
      }
    }
  }

  private handleClickOnAllResults = (event: Event): void => {
    event.preventDefault();
    this.search.submitSearch();
  };

  /* TTRQ-52361 custom start */
  private handleTrackProductClickEvent(
    e: MouseEvent,
    product: any,
    index: number
  ): void {
    e.preventDefault();
    const data = createClickEventData(
      config.flyoutConfig.searchGoogleAnalyticsContext,
      product,
      config.contextPath,
      index
    );

    trackGoogleAnalyticsEvent(EventType.productClick, data);
  }
  /* TTRQ-52361 custom end */

  render() {
    const results = store.products;
    if (results.length > 0) {
      return (
        <Host>
          {(results as Product[]) && (
            <div
              class="a-search-suggestions-wrapper"
              data-test="search-suggestions"
            >
              <div class="l-page-row">
                <ul
                  class="a-search-suggestions"
                  ref={(el) => (this.list = el)}
                  data-test="search-suggestions-list"
                >
                  {results?.map((product, index) => (
                    <li
                      class="a-search-suggestions__item a-search-suggestions__item--with-separator"
                      key={index}
                      data-test="search-suggestions-list-item"
                    >
                      <a
                        href={`${this.encodedContextPath}${product.url}`}
                        class="a-search-suggestions__result-link"
                        tabindex={-1}
                        data-test="search-suggestions-list-item-link"
                        onClick={(e) =>
                          this.handleTrackProductClickEvent(e, product, index)
                        } /* TTRQ-52361 custom */
                      >
                        {/* TT Custom start */}
                        <span class="a-search-suggestions__item-wrapper a-search-suggestions__item-wrapper-sku">
                          <span
                            class={classNames(
                              "a-search-suggestions__item-name",
                              {
                                [`a-search-suggestions__item-name--family`]:
                                  product.boschProductType === "PRODUCTFAMILY",
                              }
                            )}
                            data-test="search-suggestions-list-item-name"
                          >
                            {product.name}
                          </span>
                          <span class="a-search-suggestions__item-sku">
                            <span
                              class="a-search-suggestions__item-sku-label"
                              data-test="search-suggestions-list-item-sku"
                            >
                              {product.sku ? this.ttSkuLabel : ""}
                            </span>
                            <span
                              class="a-search-suggestions__item-sku--value"
                              data-test="search-suggestions-list-item-sku"
                            >
                              {product.boschProductType === "PRODUCTFAMILY"
                                ? "Product family"
                                : product.sku}
                            </span>
                            {product.sku && product.ean && <span> / </span>}
                            <span
                              class="a-search-suggestions__item-ean-label"
                              data-test="search-suggestions-list-item-ean"
                            >
                              {product.ean ? this.ttEanLabel : ""}
                            </span>
                            <span
                              class="a-search-suggestions__item-ean--value"
                              data-test="search-suggestions-list-item-ean"
                            >
                              {product.boschProductType === "PRODUCTFAMILY"
                                ? "Product family"
                                : product.ean}
                            </span>
                          </span>
                        </span>
                        <span class="a-search-suggestions__item-wrapper a-search-suggestions__item-wrapper-price">
                          <span class="a-search-suggestions__item-price-label">
                            {this.ttPriceLabel}{" "}
                          </span>
                          <span
                            class="a-search-suggestions__item-price"
                            data-test="search-suggestions-list-item-price"
                          >
                            {product.boschProductType !== "PRODUCTFAMILY" &&
                              product.purchasable &&
                              (product.fromPrice
                                ? `≥ ${product.fromPrice?.formattedValue}`
                                : product.price?.formattedValue || "")}
                          </span>
                        </span>
                      </a>
                      {product.purchasable && (
                        <tt-add-to-cart-mini
                          ttProductCode={product.code}
                          ttOrigin={this.ttOrigin}
                          ttMultiCartEnabled={this.ttMultiCartEnabled}
                          /* TTRQ-50439 custom start */
                          modalHeaderLabel={this.modalHeaderLabel}
                          modalBodyLabel={this.modalBodyLabel}
                          modalAddToNewWishlistLabel={
                            this.modalAddToNewWishlistLabel
                          }
                          /* TTRQ-50439 custom end */
                          /* TTRQ-52684 custom start */
                          createCartLabel={this.createCartLabel}
                          /* TTRQ-52684 custom end */
                          /* TTRQ-54327 custom start */
                          cartModalHeaderLabel={this.cartModalHeaderLabel}
                          cartModalBodyLabel={this.cartModalBodyLabel}
                          cartModalIncreaseLabel={this.cartModalIncreaseLabel}
                          cartModalNewlineLabel={this.cartModalNewlineLabel}
                          cartModalCancelLabel={this.cartModalCancelLabel}
                          /* TTRQ-54327 custom end */
                          ttPurchasableForCurrentB2BUnit={
                            product.purchasableForCurrentB2BUnit
                          } //TTRQ-56925
                          /* TTRQ-48668 Custom Start */
                          addToCartLable={this.addToCartLable}
                          addToWishlistLable={this.addToWishtLable}
                          /* TTRQ-48668 Custom End */
                        ></tt-add-to-cart-mini>
                      )}
                      {/* TT custom end */}
                    </li>
                  ))}
                  <li
                    class="a-search-suggestions__item a-search-suggestions__results-link"
                    data-test="search-suggestions-list-result"
                  >
                    <f-link
                      variant="primary"
                      href={this.allResultsUrl}
                      additionalProps='{"tabIndex":"-1"}'
                      onClick={this.handleClickOnAllResults}
                      data-test="search-suggestions-list-result-link"
                    >
                      {this.allResultsLabel}
                    </f-link>
                  </li>
                </ul>
                {/*TT Custom start */}
                {store.categories.length > 0 && (
                  <ul class="a-search-suggestions a-search-suggestions--categories">
                    <li
                      class="a-search-suggestions__item a-search-suggestions__item--with-separator"
                      key={-1}
                    >
                      <span class="a-search-suggestions__category-name">
                        {this.ttCategoriesLabel}
                      </span>
                    </li>
                    {store.categories?.map((category, index) => (
                      <li
                        class="a-search-suggestions__item a-search-suggestions__item--with-separator"
                        key={index}
                      >
                        <a
                          href={`${this.encodedContextPath}${category.categoryUrl}`}
                          class="a-search-suggestions__result-link"
                          tabIndex={-1}
                        >
                          {category.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
                {/* TT Custom end */}
              </div>
            </div>
          )}
        </Host>
      );
    }
  }
}
